<template>
  <div class="fair-survey-step fair-survey-step--swipe">
    <div class="fair-survey-step__head fair-survey-step__head--centered">
      <h2 class="fair-survey-step__title">
        {{ step.title }}
      </h2>
      <p
        v-if="step.subtitle"
        class="fair-survey-step__subtitle"
      >
        {{ step.subtitle }}
      </p>
    </div>

    <p
      v-if="isLoadingSession && cards.length === 0"
      class="fair-survey-step__note"
    >
      Загрузка образов…
    </p>

    <FlashCards
      v-else-if="cards.length > 0"
      class="tinder-like-step__deck"
      :items="cards"
      @approve="onApprove"
      @reject="onReject"
    >
      <template #default="{ item }">
        <TinderLikeCard :item="item" />
      </template>

      <template #actions="{ approve, reject }">
        <div class="tinder-like-step__actions">
          <button
            type="button"
            class="tinder-like-step__action"
            aria-label="Не нравится"
            :disabled="isSubmittingAction"
            @click="reject"
          >
            <FairIcon
              name="close"
              :size="20"
            />
          </button>
          <button
            type="button"
            class="tinder-like-step__action tinder-like-step__action--like"
            aria-label="Нравится"
            :disabled="isSubmittingAction"
            @click="approve"
          >
            <FairIcon
              name="favorite-filled"
              :size="20"
            />
          </button>
        </div>
      </template>
    </FlashCards>

    <div
      v-else-if="error"
      class="tinder-like-step__state"
    >
      <p class="fair-survey-step__note">
        {{ error }}
      </p>
      <FairButton
        label="Попробовать ещё раз"
        variant="tonal"
        size="m"
        :rounded="false"
        @click="initializeStep"
      />
    </div>

    <!-- Empty deck: nothing to swipe for these styles (or all already rated) -->
    <div
      v-else-if="!isLoadingSession"
      class="tinder-like-step__state"
    >
      <p class="fair-survey-step__note">
        {{ sessionProgress && sessionProgress.total_cards > 0
          ? 'Вы оценили все образы этого стиля'
          : 'Новых образов для этого стиля пока нет — вы оценили всё, что есть, или их ещё не добавили' }}
      </p>
      <FairButton
        :label="isLastStep ? 'Готово' : 'Продолжить'"
        variant="primary"
        size="m"
        :rounded="false"
        @click="finishStep"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { useTasteClarificationModal } from '@/features/taste/store'
import type { TasteStep } from '@/features/taste/types/taste-config'
import { startTasteMatchingSession, fetchSessionCards, submitSwipeOptimized } from '@/features/taste/api/taste-matching'
import { adaptSessionCard, getStyleIdsFromTasteData, getGenderFromTasteData } from '@/features/taste/utils/taste-matching'
import { queryCache } from '@/queries/core/queryCache'

import { FlashCards } from '@/components/HelpersComponents/Cards'
import TinderLikeCard from '@/components/HelpersComponents/Cards/components/TinderLikeCard.vue'
import { FairButton } from '@/ui/primitives/FairButton'
import { FairIcon } from '@/ui/primitives/FairIcon'

interface Props {
  step: TasteStep & { type: 'tinder_like' }
}

const props = defineProps<Props>()
const tasteStore = useTasteClarificationModal()
const router = useRouter()

// Состояние
const cards = ref<Array<{ id: number; text: string; description: string; image: string; originalId?: string }>>([])
const sessionId = ref<string | null>(null) // ID сессии свайпов из API
const sessionProgress = ref<{ total_cards: number; swiped_in_session: number; remaining_in_session: number } | null>(null)
const isSessionComplete = ref(false) // Флаг завершения сессии
const isLoadingSession = ref(false)
const isSubmittingAction = ref(false)
const error = ref<string | null>(null)

const currentStepIndex = computed(() => tasteStore.tasteModal.currentStepIndex)
const totalSteps = computed(() => tasteStore.totalSteps)
const isLastStep = computed(() => currentStepIndex.value === totalSteps.value - 1)

// Отслеживание времени показа карточки для swipe_time_ms
const cardShowTimes = ref<Map<number, number>>(new Map())

/**
 * Запуск сессии свайпов и получение первых карточек
 */
async function startSession() {
  isLoadingSession.value = true
  error.value = null

  try {
    // Получаем style_ids из шага "style-evaluation-choice"
    const styleIds = getStyleIdsFromTasteData(tasteStore.tasteModal.tasteData)

    if (styleIds.length === 0) {
      throw new Error('Не выбраны стили для оценки. Пожалуйста, вернитесь и выберите стили.')
    }

    // Получаем gender из шага "gender"
    const gender = getGenderFromTasteData(tasteStore.tasteModal.tasteData)

    // Запускаем сессию с режимом sequential (можно сделать настраиваемым)
    const sessionResponse = await startTasteMatchingSession(styleIds, 'sequential', gender || undefined)
    // Defensive: handle both camelCase and snake_case response keys
    const resolvedSessionId = sessionResponse.sessionId || (sessionResponse as any).session_id
    if (!resolvedSessionId) {
      throw new Error('Сервер не вернул идентификатор сессии')
    }
    sessionId.value = resolvedSessionId

    // Сохраняем session_id в store для возможного восстановления — ВМЕСТЕ с
    // набором стилей, для которых она запущена: сессия одного стиля не должна
    // восстанавливаться для другого.
    const stepKey = `${props.step.id}_sessionId`
    tasteStore.updateTasteData(stepKey, sessionId.value)
    tasteStore.updateTasteData(`${props.step.id}_sessionStyles`, sessionStylesKey(styleIds))

    // Получаем первые карточки для сессии.
    // Берём локальную `resolvedSessionId`, а не `sessionId.value`: выше она уже
    // проверена на пустоту, а сужение по ref через присваивание TS не отследит.
    const cardsResponse = await fetchSessionCards(resolvedSessionId)

    // Проверяем, что все карточки имеют обязательный id
    for (const card of cardsResponse.cards) {
      if (card.id === undefined || card.id === null) {
        throw new Error('Карточка не содержит обязательный id')
      }
    }

    // Адаптируем карточки для FlashCards компонента
    const adaptedCards = cardsResponse.cards.map(adaptSessionCard)
    cards.value = adaptedCards

    // Записываем время показа для каждой карточки
    cardsResponse.cards.forEach((card) => {
      cardShowTimes.value.set(card.id, Date.now())
    })

    // Инициализируем прогресс сессии
    sessionProgress.value = {
      total_cards: sessionResponse.totalCards,
      swiped_in_session: 0,
      remaining_in_session: sessionResponse.totalCards
    }
    
    // Сохраняем прогресс в store
    const progressKey = `${props.step.id}_sessionProgress`
    tasteStore.updateTasteData(progressKey, sessionProgress.value)
  } catch (err) {
    console.error('Error starting session:', err)
    error.value = err instanceof Error ? err.message : 'Не удалось запустить сессию'
  } finally {
    isLoadingSession.value = false
  }
}

/**
 * Восстановление сессии по сохраненному session_id
 */
async function restoreSession(savedSessionId: string) {
  isLoadingSession.value = true
  error.value = null

  try {
    // Пытаемся получить карточки для существующей сессии
    const cardsResponse = await fetchSessionCards(savedSessionId)
    sessionId.value = savedSessionId

    // Адаптируем карточки
    const adaptedCards = cardsResponse.cards.map(adaptSessionCard)
    cards.value = adaptedCards

    // Записываем время показа
    cardsResponse.cards.forEach((card) => {
      cardShowTimes.value.set(card.id, Date.now())
    })

    // Прогресс восстановленной сессии — из её ответа, не "после первого свайпа":
    // без этого шапка показывала "0 из 0" на уже пройденной колоде.
    const restored = cardsResponse.sessionProgress
    sessionProgress.value = restored
      ? {
          total_cards: restored.totalCards,
          swiped_in_session: restored.swipedInSession,
          remaining_in_session: restored.remainingInSession
        }
      : {
          total_cards: adaptedCards.length,
          swiped_in_session: 0,
          remaining_in_session: adaptedCards.length
        }
    tasteStore.updateTasteData(`${props.step.id}_sessionProgress`, sessionProgress.value)
    if (adaptedCards.length === 0) {
      isSessionComplete.value = true
    }
  } catch (err) {
    console.error('Error restoring session:', err)
    // Если сессия не найдена, создаем новую
    await startSession()
  } finally {
    isLoadingSession.value = false
  }
}

/** Стабильный ключ набора стилей сессии (порядок не важен). */
function sessionStylesKey(ids: number[]): string {
  return [...ids].sort((a, b) => a - b).join(',')
}

/**
 * Явный выход из пустой/пройденной колоды — вместо немого модала.
 */
function finishStep() {
  if (isLastStep.value) {
    tasteStore.hideTasteModal()
    router.push('/profile/my-style')
  } else {
    tasteStore.nextStep()
  }
}

/**
 * Проверка завершения сессии
 */
async function checkCompletion() {
  await nextTick() // Ждем обновления реактивного состояния
  
  if (isSessionComplete.value && sessionProgress.value) {
    // Все карточки обработаны
    if (isLastStep.value) {
      // Если это последний шаг, перенаправляем на страницу профиля
      tasteStore.hideTasteModal()
      router.push('/profile/my-style')
    } else {
      // Переходим к следующему шагу
      tasteStore.nextStep()
    }
  }
}

/**
 * Обработка действия пользователя (лайк/дизлайк) через оптимизированный API
 */
async function handleCardAction(card: { id: number; text?: string; description?: string; image?: string; originalId?: string }, action: 'like' | 'dislike') {
  if (!sessionId.value) {
    console.error('[TinderLikeStep] Session id is missing')
    error.value = 'Сессия не инициализирована'
    return
  }

  const cardId = card.id

  // Вычисляем swipe_time_ms (время от показа карточки до свайпа)
  const showTime = cardShowTimes.value.get(cardId)
  const swipeTimeMs = showTime ? Date.now() - showTime : undefined

  isSubmittingAction.value = true
  error.value = null

  try {
    // Отправляем свайп через оптимизированный API
    const response = await submitSwipeOptimized(
      cardId,
      sessionId.value,
      action,
      swipeTimeMs
    )

    // Удаляем время показа карточки
    cardShowTimes.value.delete(cardId)

    // Обновляем прогресс сессии
    if (response.sessionProgress) {
      sessionProgress.value = {
        total_cards: response.sessionProgress.totalCards,
        swiped_in_session: response.sessionProgress.swipedInSession,
        remaining_in_session: response.sessionProgress.remainingInSession
      }
      
      // Сохраняем прогресс в store
      const progressKey = `${props.step.id}_sessionProgress`
      tasteStore.updateTasteData(progressKey, sessionProgress.value)
    }

    // Если есть следующая карточка, добавляем её в массив
    if (response.nextCard) {
      const adaptedCard = adaptSessionCard(response.nextCard)
      cards.value.push(adaptedCard)
      // Записываем время показа новой карточки
      cardShowTimes.value.set(response.nextCard.id, Date.now())
    }

    // Сохраняем прогресс в store
    const stepKey = `${props.step.id}_swipedCount`
    if (response.sessionProgress) {
      tasteStore.updateTasteData(stepKey, response.sessionProgress.swipedInSession)
    }

    // Проверяем, завершена ли сессия
    if (response.sessionComplete) {
      isSessionComplete.value = true
      // Invalidate profile data so level/progress is refreshed
      queryCache.invalidate(['user-profile', 'profile-data'])
      await checkCompletion()
    }
  } catch (err) {
    console.error('[TinderLikeStep] Error submitting card action:', err)
    error.value = err instanceof Error ? err.message : 'Не удалось отправить действие'
    // В случае ошибки не продолжаем, чтобы пользователь мог повторить
  } finally {
    isSubmittingAction.value = false
  }
}

function onApprove(card: { id?: number; text?: string; description?: string; image?: string; originalId?: string }) {
  // Проверяем, что id существует (может быть 0, что валидно)
  if (card.id === undefined || card.id === null) {
    console.warn('[TinderLikeStep] Card id is missing in onApprove')
    return
  }
  handleCardAction(card as { id: number; text?: string; description?: string; image?: string; originalId?: string }, 'like')
}

function onReject(card: { id?: number; text?: string; description?: string; image?: string; originalId?: string }) {
  // Проверяем, что id существует (может быть 0, что валидно)
  if (card.id === undefined || card.id === null) {
    console.warn('[TinderLikeStep] Card id is missing in onReject')
    return
  }
  handleCardAction(card as { id: number; text?: string; description?: string; image?: string; originalId?: string }, 'dislike')
}

/**
 * Инициализация при монтировании или изменении step
 */
async function initializeStep() {
  // Сбрасываем состояние
  cards.value = []
  error.value = null
  isLoadingSession.value = false
  isSubmittingAction.value = false
  cardShowTimes.value.clear()
  sessionProgress.value = null
  isSessionComplete.value = false
  
  // Очищаем прогресс в store
  const progressKey = `${props.step.id}_sessionProgress`
  tasteStore.updateTasteData(progressKey, null)

  // Проверяем, есть ли сохраненная сессия — и что она ЭТИХ стилей. Прогресс
  // прошлого опроса переживает localStorage: без проверки открытие "Деловой"
  // восстанавливало исчерпанную сессию "Кэжуал" и сразу показывало
  // "No more cards" (founder, 2026-08-22).
  const sessionKey = `${props.step.id}_sessionId`
  const savedSessionId = tasteStore.tasteModal.tasteData[sessionKey]
  const savedStyles = tasteStore.tasteModal.tasteData[`${props.step.id}_sessionStyles`]
  const currentStyles = sessionStylesKey(getStyleIdsFromTasteData(tasteStore.tasteModal.tasteData))
  const sessionBelongsHere = typeof savedStyles === 'string' && savedStyles === currentStyles

  if (savedSessionId && typeof savedSessionId === 'string' && sessionBelongsHere) {
    // Пытаемся восстановить существующую сессию
    await restoreSession(savedSessionId)
  } else {
    // Создаем новую сессию
    await startSession()
  }
}

// Инициализация при монтировании
onMounted(() => {
  initializeStep()
})

// Повторная инициализация при изменении step
watch(() => props.step.id, () => {
  initializeStep()
}, { immediate: false })
</script>
<style scoped>
.fair-survey-step--swipe {
  height: 100%;
  align-items: center;
  gap: var(--space-16);
}

.tinder-like-step__deck {
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
}

.tinder-like-step__actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-16);
  padding-top: var(--space-16);
}

/* Both verdicts get the same weight — the icon carries the meaning. */
.tinder-like-step__action {
  display: inline-flex;
  width: 56px;
  height: 56px;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0;
  border: 1px solid var(--color-line-hairline);
  border-radius: var(--radius-full);
  background: var(--color-bg-surface);
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease;
}

.tinder-like-step__action:hover:not(:disabled) {
  border-color: var(--color-border-control);
  color: var(--color-text-primary);
}

.tinder-like-step__action--like:hover:not(:disabled) {
  color: var(--color-signal-positive-text);
}

.tinder-like-step__action:disabled {
  color: var(--color-disabled);
  cursor: default;
}

.tinder-like-step__state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-16);
  margin: auto 0;
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .tinder-like-step__action {
    transition: none;
  }
}
</style>
